import React, { useState, useEffect, useRef } from "react"; // Add useRef import
import "./styles.css";
import { ASSETS } from "../../utils/config";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector } from "react-redux";
import { WhatsappShareButton, WhatsappIcon } from "react-share";
import { toast } from "react-toastify";
import WheelComponent from "../WheelComponent";
import { useGetPrimaryWheelV2 } from "../../services/queries";
import styled from "styled-components";
import TextView from "../TextView";
import { useNavigate } from "react-router-dom";
import UserImage from "../UserImage";

const MobileContainer = styled.div`
  width: 90vw;
  max-width: 400px;
  height: auto;
  background: #ffffff;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    width: 80vw;
    max-width: 500px;
  }

  @media (min-width: 1024px) {
    width: 70vw;
    max-width: 600px;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #d9d9d9;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background: #ffffff;

  h2 {
    font-size: 18px;
    margin: 0;
  }

  button {
    font-size: 24px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    color: #666;
  }
`;

const MobileContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  .invite-message-section {
    width: 100%;
  }

  .link-section {
    width: 100%;
  }

  .link-container input {
    width: 100%;
    padding: 8px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    font-size: 14px;
  }
`;

const MobileShareOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;
  background: #f6f7f9;
  border-radius: 16px;
  width: 60%;
  align-self: center;
`;

const ReferralContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  gap: 15px;
  background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.7),
      rgba(255, 255, 255, 0.7)
    ),
    conic-gradient(
      from 180deg at 50% 50%,
      #f5eede -41.73deg,
      #ccfeff 37.8deg,
      #f7fed5 140.4deg,
      #fdcffe 216deg,
      #f5eede 318.27deg,
      #ccfeff 397.8deg
    );
  border: 1px solid #b8b8b8;
  border-radius: 8px;
  margin: 16px;
`;

const ReferralButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 300px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  border: none;

  &.join-now-btn {
    background: #9b5af8;
    color: white;
  }

  &.share-anyway-btn {
    background: white;
    color: #9b5af8;
    border: 1px solid #9b5af8;
  }
`;

const MobileVersion = ({ isVisible, onClose, wheelToRender }) => {
  const { userProfile } = useSelector((state) => state.userProfile);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const nav = useNavigate();
  const modalRef = useRef(null); // Add ref for the modal container

  const {
    data: primaryWheel,
    isLoading,
    isError,
    error,
  } = useGetPrimaryWheelV2(userProfile?.user_id);

  useEffect(() => {
    if (isVisible) {
      // Set showShareOptions to true if user is in Earnings Program
      if (userProfile?.is_in_referral_program) {
        setShowShareOptions(true);
      }
    }
    if (!isVisible) {
      setShowShareOptions(false);
    }
  }, [isVisible, userProfile?.is_in_referral_program]);

  function capitalize(str) {
    return str?.charAt(0).toUpperCase() + str?.slice(1);
  }
  const getDomain = () => {
    const url = window.location.href;
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}`;
  };

  const currentUrl = getDomain();
  const referralUrl = userProfile?.is_in_referral_program
    ? `${currentUrl}/signup/${userProfile?.code}`
    : `${currentUrl}/signup`;
  const quote = "Check out this amazing content!";

  const inviteMessage = `${capitalize(
    userProfile?.username
  )} has invited you to get your own free iYuga account!... ${referralUrl}`;

  const copyToClipboard = (toCopyText = inviteMessage) => {
    navigator.clipboard
      .writeText(toCopyText)
      .then(() => {
        toast.success("Message copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy message: ", err);
      });
  };

  const handleEmailShare = () => {
    const subject = `${capitalize(
      userProfile?.username
    )} has invited you to get your own free iYuga account!...`;
    const body = `${referralUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  // Add handler for overlay clicks
  const handleOverlayClick = (event) => {
    // If clicking the overlay (not the modal itself), close the modal
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={handleOverlayClick} // Add the click handler here
    >
      <MobileContainer ref={modalRef}>
        {" "}
        {/* Add the ref to the container */}
        <MobileHeader>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img
              src={ASSETS.InviteShare}
              alt="Add Group"
              style={{ width: "24px", height: "24px" }}
            />
            <h2>{!showShareOptions ? "Invite Friends" : "Share iYuga"}</h2>
          </div>
          <button onClick={onClose}>×</button>
        </MobileHeader>
        <MobileContent>
          {!showShareOptions ? (
            <ReferralContent>
              <div className="referral-icon">
                <img
                  src={ASSETS.Referrals}
                  alt="Add Group"
                  style={{ width: "32px", height: "32px" }}
                />
              </div>
              <p
                className="referral-text"
                style={{
                  textAlign: "center",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                You're about to invite somebody to enjoy the iYuga app! Want to
                get paid for sharing? Join our Earnings Program, available to
                all Premium or higher subscribers.
              </p>
              <ReferralButtons>
                <Button
                  className="join-now-btn"
                  onClick={() => nav("/referrals")}
                >
                  Join Now
                </Button>
                <Button
                  className="share-anyway-btn"
                  onClick={() => setShowShareOptions(true)}
                >
                  Invite Anyway
                </Button>
              </ReferralButtons>
            </ReferralContent>
          ) : (
            <>
              <div className="invite-message-section">
                <div
                  className="invite-message-header"
                  style={{ width: "80vw" }}
                >
                  <h3>Invite Message with Link</h3>
                  <button
                    className="copy-link"
                    onClick={() => copyToClipboard(inviteMessage)}
                  >
                    <ContentCopyIcon
                      style={{ color: "#9B5AF8", fontSize: "15px" }}
                    />
                  </button>
                </div>
                <div
                  className="invite-message-content"
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div className="user-avatar" style={{ margin: "0 auto" }}>
                    {wheelToRender ? (
                      <WheelComponent
                        userData={{
                          id: Math.random(),
                          url: userProfile?.profile_image || ASSETS.iyugaLogo,
                        }}
                        maxSelectedArcs={0}
                        arcThickness={-10}
                        chartData={wheelToRender}
                        margin={1}
                        setChartData={() => {}}
                        containerDivStyle={{
                          width: "100%",
                          height: "100%",
                        }}
                        config={{
                          showTooltips: false,
                        }}
                      />
                    ) : (
                      <UserImage image={userProfile?.profile_image} size={60} />
                    )}
                  </div>
                  <p
                    className="invite-text"
                    style={{ width: "100%", textAlign: "center" }}
                  >
                    {capitalize(userProfile?.username)} has invited you to join
                    iYuga! Click here to Bundle, Share, and Engage:{" "}
                    <a
                      href={referralUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="invite-link"
                    >
                      {referralUrl}
                    </a>
                  </p>
                </div>
              </div>

              <div className="link-section">
                <div className="link-header">
                  <h3>Link Only</h3>
                  <button
                    className="copy-link"
                    onClick={() => copyToClipboard(referralUrl)}
                  >
                    <ContentCopyIcon
                      style={{ color: "#9B5AF8", fontSize: "15px" }}
                    />
                  </button>
                </div>
                <div className="link-container">
                  <TextView>{referralUrl}</TextView>
                </div>
              </div>

              {/* <MobileShareOptions>
                <div className="share-option" onClick={handleEmailShare}>
                  <div className="share-icon email">
                    <MailRoundedIcon
                      style={{ color: "white", fontSize: "20px" }}
                    />
                  </div>
                  <span>Mail</span>
                </div>

                <div className="share-option">
                  <WhatsappShareButton url={referralUrl} title={inviteMessage}>
                    <div className="share-icon whatsapp">
                      <WhatsappIcon size={32} round />
                    </div>
                  </WhatsappShareButton>
                  <span>Whatsapp</span>
                </div>
              </MobileShareOptions> */}
            </>
          )}
        </MobileContent>
      </MobileContainer>
    </div>
  );
};

export default MobileVersion;
